import type { RewardItem } from "./reward";
import type { MsgKey } from "./i18n";
import type { I18nText } from "./locales";

/**
 * 卡牌配置。两种卡共用一套结构，差异只在解锁/集齐字段：
 * - 物品卡包：集齐后给 setReward
 * - idol 卡包：靠集齐某个物品卡包解锁（unlockPack）
 *
 * 列表的一行是**卡包**不是单卡：策划按包配，单卡在弹窗里逐张填。
 */
export type CardKind = "item" | "idol";

export interface Card {
  code: string;
  name: string;
  i18n: I18nText;
  /** 掉落权重，越大越容易出 */
  weight: number | null;
  /** 稀有度星级 1~5 */
  rarity: number;
  /** 图片资源标识；未上传为空 */
  image?: string | null;
}

export interface CardPack {
  id: string;
  code: string;
  name: string;
  i18n: I18nText;
  /** 卡片数量：这一包应该有几张，与 cards.length 对不上时要提示 */
  count: number | null;
  cover: boolean;
  /** 封面图 R2 key（空 = 未传） */
  coverImg?: string;
  cards: Card[];
  /** 物品卡包专有：集齐奖励的展示文本（由 setRewards 生成，兼容旧列 set_reward） */
  setReward?: string;
  /** 物品卡包专有：集齐奖励（结构化，与通关奖励同形状；玩家集齐后点「领取」才发） */
  setRewards?: RewardItem[];
  /** idol 卡包专有：解锁所需的物品卡包 code */
  unlockPack?: string | null;
  /** idol 卡包专有：所属 idol（idol 列表的 idolId）；null = 未绑定。可选，不必填（leo 2026-08-27） */
  idolId?: string | null;
  /** idol 卡包专有：商城买一次按包内权重抽几张（默认 6） */
  shopDrawCount?: number | null;
  /** idol 卡包专有：商城买一次扣多少钻石（0 = 免费，只应出现在测试环境） */
  shopPriceGem?: number | null;
  /**
   * 禁用：不参与掉落、也不作为奖励发放；**已在玩家背包里的卡牌不受影响**。
   * 单独一个状态位而不是直接删包——删了以后玩家背包里那些卡就没有出处了。
   */
  disabled?: boolean;
  /** 可被万能卡兑换（leo 已定：可兑范围配置化，物品/idol 包同一开关） */
  wildcardOk?: boolean;
  /** 旧出包权重仅保留数据，不参与玩家等级概率选包；包内卡牌权重仍生效 */
  weight?: number | null;
  /** 玩家等级掉落：同等级合计100%。rate 是 UI 态的百分数字符串（服务端存 rate_bps） */
  drops?: PackDrop[];
  /** 旧逐星数据，仅供重新配置等级概率时核对；保存时清除 */
  starDrops?: PackDrop[] | null;
}

export interface PackDrop { id: string; level: number; rate: string }

/** 哪些 idol 卡包把这个物品卡包当解锁条件。被引用的物品卡包不能直接删 */
export const idolRefsOf = (itemCode: string, idolPacks: CardPack[]) =>
  idolPacks.filter((p) => p.unlockPack === itemCode);

/** 列表按编号的数字大小排（编号是数字串，按字符串排 2 会在 1063 后面）；非数字的排在后面按字面 */
export const byPackCode = (a: CardPack, b: CardPack) => {
  const na = /^[0-9]+$/.test(a.code) ? Number(a.code) : Infinity, nb = /^[0-9]+$/.test(b.code) ? Number(b.code) : Infinity;
  return na - nb || (na === Infinity && nb === Infinity ? a.code.localeCompare(b.code) : 0) || a.id.localeCompare(b.id);
};

export const rarityStars = (n: number) => "★".repeat(n) + "☆".repeat(Math.max(0, 5 - n));

/**
 * 新建卡包。编号必须避开这一版里已有的所有卡包（物品 + idol）：保存按编号整包写（服务端是 upsert），
 * 撞上已有编号 = 用空包把那一包覆盖掉。
 * - packId 带随机后缀：几个人同时在各自的页面里新建，看到的已有编号相同，按「最大号 + 1」取会撞
 * - code 取已有数字编号的最大值 + 1，作为新增抽屉里的建议值，运营可改；名字留空由运营填
 */
export function newPack(kind: CardKind, existing: CardPack[], rand: () => string = () => Math.random().toString(36).slice(2, 10)): CardPack {
  const taken = new Set(existing.map((p) => p.id));
  let id = `np-${rand()}`;
  while (taken.has(id)) id = `np-${rand()}`;
  const seq = existing.reduce((m, p) => (/^[0-9]+$/.test(p.code) ? Math.max(m, Number(p.code)) : m), 100) + 1;
  return {
    id,
    code: String(seq),
    name: "",
    i18n: {},
    count: null,
    cover: false,
    cards: [],
    ...(kind === "idol" ? { unlockPack: null, idolId: null, shopDrawCount: 6, shopPriceGem: 0 } : { setReward: "", setRewards: [] }),
  };
}

/** 新增抽屉点「新增」前要过的几项：编号、英文名、张数、idol 包的解锁条件（同设计稿 cardpage.jsx NewPackDialog） */
export function newPackIssues(p: CardPack, kind: CardKind, takenCodes: string[]): PackIssue[] {
  const out: PackIssue[] = [];
  const code = p.code.trim();
  if (!p.name.trim()) out.push({ key: "pack.new.needName" });
  if (!/^[0-9]+$/.test(code)) out.push({ key: "pack.new.badCode" });
  else if (takenCodes.includes(code)) out.push({ key: "pack.new.codeTaken", vars: { code } });
  if (!(p.count != null && p.count > 0)) out.push({ key: "pack.new.needCount" });
  if (kind === "idol" && !p.unlockPack) out.push({ key: "pack.new.needUnlock" });
  return out;
}

export function newCard(packCode: string, idx: number): Card {
  return { code: `${packCode}-${idx}`, name: `Card ${idx}`, i18n: {}, weight: null, rarity: 1, image: null };
}

/**
 * 包里加一张卡：序号取已有「<包编号>-N」里最大的 N + 1。不能按张数 + 1——删掉中间一张再加，
 * 新卡会和最后一张同号（玩家收集进度按卡牌编号记，发布闸 CARD_CODE_UNIQUE 也会拦）
 */
export function nextCard(packCode: string, cards: readonly Card[]): Card {
  const max = cards.reduce((m, c) => {
    const hit = c.code.startsWith(`${packCode}-`) ? c.code.slice(packCode.length + 1) : "";
    return /^[0-9]+$/.test(hit) ? Math.max(m, Number(hit)) : m;
  }, 0);
  let idx = Math.max(max, cards.length) + 1;
  while (cards.some((c) => c.code === `${packCode}-${idx}`)) idx++;
  return newCard(packCode, idx);
}

/** 卡包是否配齐：数量对得上、每张都有名字和权重 */
export interface PackIssue { key: MsgKey; vars?: Record<string, string | number> }

export function packIssues(p: CardPack): PackIssue[] {
  const out: PackIssue[] = [];
  if (p.count == null) out.push({ key: "pack.noCount" });
  else if (p.cards.length !== p.count) out.push({ key: "pack.countMismatch", vars: { have: p.cards.length, want: p.count } });
  const noName = p.cards.filter((c) => !c.name.trim()).length;
  if (noName) out.push({ key: "pack.noName", vars: { n: noName } });
  const noWeight = p.cards.filter((c) => c.weight == null).length;
  if (noWeight) out.push({ key: "pack.noWeight", vars: { n: noWeight } });
  return out;
}
